import React, { useState, useEffect } from 'react';

function LegalPages({ activePage = 'terms' }) {
  const [currentPage, setCurrentPage] = useState(activePage);

  useEffect(() => {
    setCurrentPage(activePage);
  }, [activePage]);

  const PageHeader = ({ title, icon }) => (
    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
      <div style={{ fontSize: '48px', marginBottom: '15px' }}>{icon}</div>
      <h1 style={{
        fontSize: '32px',
        fontWeight: '700',
        background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '8px'
      }}>
        {title}
      </h1>
      <p style={{
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '16px',
        margin: 0
      }}>
        Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
    </div>
  );

  const SectionTitle = ({ children }) => (
    <h2 style={{
      color: '#00E9FF',
      fontSize: '24px',
      fontWeight: '600',
      marginTop: '40px',
      marginBottom: '20px'
    }}>
      {children}
    </h2>
  );

  const SectionContent = ({ children }) => (
    <div style={{
      color: 'rgba(255, 255, 255, 0.9)',
      fontSize: '16px',
      lineHeight: '1.8',
      marginBottom: '25px'
    }}>
      {children}
    </div>
  );

  const renderTermsContent = () => (
    <div>
      <PageHeader title="Terms & Conditions" icon="📋" />
      
      <SectionTitle>1. Acceptance of Terms</SectionTitle>
      <SectionContent>
        By using Flirteen, you agree to these terms. If you don't agree, please don't use our service. These terms may be updated periodically, and continued use constitutes acceptance of any changes.
      </SectionContent>

      <SectionTitle>2. Service Description</SectionTitle>
      <SectionContent>
        Flirteen is a dating platform that connects people looking for relationships. We provide profile creation, matching algorithms, messaging, and premium features to enhance your dating experience.
      </SectionContent>

      <SectionTitle>3. User Responsibilities</SectionTitle>
      <SectionContent>
        <strong>You agree to:</strong><br/>
        • Provide accurate information in your profile<br/>
        • Be respectful to other users<br/>
        • Not misrepresent yourself or use fake photos<br/>
        • Report inappropriate behavior<br/>
        • Use the service for legitimate dating purposes only
      </SectionContent>

      <SectionTitle>4. Prohibited Activities</SectionTitle>
      <SectionContent>
        <strong>The following are strictly prohibited:</strong><br/>
        • Harassment, abuse, or threatening behavior<br/>
        • Spam, commercial activities, or solicitation<br/>
        • Sharing explicit or inappropriate content<br/>
        • Creating multiple accounts or fake profiles<br/>
        • Attempting to harm or exploit other users
      </SectionContent>

      <SectionTitle>5. Premium Services</SectionTitle>
      <SectionContent>
        Premium subscriptions provide additional features. Subscriptions are billed monthly and can be cancelled anytime. Refunds are provided according to our refund policy. Premium features are subject to change.
      </SectionContent>

      <SectionTitle>6. Limitation of Liability</SectionTitle>
      <SectionContent>
        Flirteen is provided "as is" without warranties. We are not responsible for user interactions, relationships formed, or any damages arising from use of the service. Users interact at their own risk and discretion.
      </SectionContent>

      <SectionTitle>7. Account Termination</SectionTitle>
      <SectionContent>
        We may suspend or terminate accounts that violate these terms. Users may delete their accounts at any time by contacting us. Upon termination, user data will be handled according to our privacy policy.
      </SectionContent>

      <SectionTitle>8. Contact Us</SectionTitle>
      <SectionContent>
        Questions about these terms? Contact us at <a href="mailto:legal@flirteen.com" style={{ color: '#00E9FF' }}>legal@flirteen.com</a>
      </SectionContent>
    </div>
  );

  const renderPrivacyContent = () => (
    <div>
      <PageHeader title="Privacy Policy" icon="🔒" />

      <SectionTitle>1. Information We Collect</SectionTitle>
      <SectionContent>
        <strong>Profile Information:</strong> Name, age, location, photos, bio, and preferences you provide.<br/>
        <strong>Usage Data:</strong> How you interact with the app, matches, messages (content is private).<br/>
        <strong>Device Information:</strong> Basic device and browser information for security and functionality.
      </SectionContent>

      <SectionTitle>2. How We Use Your Information</SectionTitle>
      <SectionContent>
        • <strong>Matching:</strong> To suggest compatible profiles based on your preferences<br/>
        • <strong>Communication:</strong> To enable messaging between matched users<br/>
        • <strong>Safety:</strong> To prevent fraud and maintain a safe environment<br/>
        • <strong>Improvement:</strong> To enhance our algorithms and user experience
      </SectionContent>

      <SectionTitle>3. Information Sharing</SectionTitle>
      <SectionContent>
        <strong>We DO NOT sell your personal data.</strong><br/>
        • Your profile is visible to other users as intended by the service<br/>
        • We may share aggregated, non-personal statistics<br/>
        • We comply with legal requests when required by law<br/>
        • Service providers help us operate the platform under strict confidentiality
      </SectionContent>

      <SectionTitle>4. Data Security</SectionTitle>
      <SectionContent>
        We implement industry-standard security measures to protect your data. However, no system is 100% secure. We encourage users to practice safe online habits and report any security concerns immediately.
      </SectionContent>

      <SectionTitle>5. Your Rights</SectionTitle>
      <SectionContent>
        • <strong>Access:</strong> View your personal data<br/>
        • <strong>Correction:</strong> Update inaccurate information<br/>
        • <strong>Deletion:</strong> Delete your account and associated data<br/>
        • <strong>Control:</strong> Manage privacy settings and preferences
      </SectionContent>

      <SectionTitle>6. Data Retention</SectionTitle>
      <SectionContent>
        We retain your data while your account is active. After account deletion, most data is removed within 30 days, except where required for legal compliance or safety purposes.
      </SectionContent>

      <SectionTitle>7. Contact Us</SectionTitle>
      <SectionContent>
        Privacy questions? Contact us at <a href="mailto:privacy@flirteen.com" style={{ color: '#00E9FF' }}>privacy@flirteen.com</a>
      </SectionContent>
    </div>
  );

  const renderSafetyContent = () => (
    <div>
      <PageHeader title="Safety Guidelines" icon="🛡️" />

      <SectionTitle>Your Safety is Our Priority</SectionTitle>
      <SectionContent>
        Dating should be fun and safe. Follow these guidelines to protect yourself and create a positive experience for everyone on Flirteen.
      </SectionContent>

      <SectionTitle>🔒 Profile Safety</SectionTitle>
      <SectionContent>
        • Use recent, authentic photos of yourself<br/>
        • Don't share personal information like address, phone number, or workplace in your profile<br/>
        • Be honest about your intentions and what you're looking for<br/>
        • Report profiles that seem fake or suspicious
      </SectionContent>

      <SectionTitle>💬 Communication Safety</SectionTitle>
      <SectionContent>
        • Keep conversations on the platform initially<br/>
        • Be cautious of people who ask for money or personal information<br/>
        • Trust your instincts - if something feels wrong, it probably is<br/>
        • Report harassment, inappropriate messages, or suspicious behavior immediately
      </SectionContent>

      <SectionTitle>☕ Meeting Safety Tips</SectionTitle>
      <SectionContent>
        <strong>Before Meeting:</strong><br/>
        • Video chat or phone call first to verify identity<br/>
        • Tell a friend or family member about your plans<br/>
        • Share your location with trusted contacts<br/><br/>

        <strong>First Date Safety:</strong><br/>
        • Meet in public places during daytime<br/>
        • Drive yourself or use your own transportation<br/>
        • Don't share your home address initially<br/>
        • Stay sober and alert during the date
      </SectionContent>

      <SectionTitle>⚠️ Red Flags to Watch For</SectionTitle>
      <SectionContent>
        • Requests for money, gifts, or financial information<br/>
        • Pressure to move conversations off the platform quickly<br/>
        • Refusal to video chat or talk on the phone<br/>
        • Inconsistent stories or information<br/>
        • Aggressive or manipulative behavior<br/>
        • Requests for explicit photos or content
      </SectionContent>

      <SectionTitle>🚨 How to Report</SectionTitle>
      <SectionContent>
        If you encounter inappropriate behavior:<br/>
        • Use the report button on profiles and messages<br/>
        • Block users who make you uncomfortable<br/>
        • Contact our safety team at <a href="mailto:safety@flirteen.com" style={{ color: '#00E9FF' }}>safety@flirteen.com</a><br/>
        • For emergencies, contact local authorities immediately
      </SectionContent>

      <SectionTitle>💡 Additional Resources</SectionTitle>
      <SectionContent>
        Remember: You have the right to feel safe and respected. Trust your instincts, take your time getting to know people, and never feel pressured to do anything that makes you uncomfortable.
      </SectionContent>
    </div>
  );

  const renderCommunityContent = () => (
    <div>
      <PageHeader title="Community Guidelines" icon="👥" />

      <SectionTitle>Building a Respectful Community</SectionTitle>
      <SectionContent>
        Flirteen is a community where people come to find meaningful connections. These guidelines help us maintain a positive, respectful environment for everyone.
      </SectionContent>

      <SectionTitle>✨ Be Authentic</SectionTitle>
      <SectionContent>
        • Use real, recent photos of yourself<br/>
        • Be honest in your profile and conversations<br/>
        • Represent yourself accurately - age, interests, intentions<br/>
        • One person, one account - fake or duplicate accounts are not allowed
      </SectionContent>

      <SectionTitle>💕 Treat Others with Respect</SectionTitle>
      <SectionContent>
        • Be kind and courteous in all interactions<br/>
        • Respect when someone isn't interested<br/>
        • Don't send unsolicited explicit content<br/>
        • Avoid discriminatory language or behavior<br/>
        • Remember there's a real person behind every profile
      </SectionContent>

      <SectionTitle>🚫 What's Not Allowed</SectionTitle>
      <SectionContent>
        <strong>Prohibited Content & Behavior:</strong><br/>
        • Harassment, bullying, or threatening language<br/>
        • Explicit sexual content or nudity<br/>
        • Hate speech or discrimination<br/>
        • Spam, scams, or commercial solicitation<br/>
        • Impersonation or catfishing<br/>
        • Sharing personal information of others without consent
      </SectionContent>

      <SectionTitle>💼 Keep It Personal</SectionTitle>
      <SectionContent>
        Flirteen is for personal relationships, not business:<br/>
        • No advertising or promoting services<br/>
        • Don't solicit money or gifts<br/>
        • No MLM schemes or business opportunities<br/>
        • Keep conversations focused on getting to know each other
      </SectionContent>

      <SectionTitle>🎯 Dating Etiquette</SectionTitle>
      <SectionContent>
        • Be clear about your intentions<br/>
        • Communicate openly and honestly<br/>
        • If you're not interested, decline politely<br/>
        • Don't ghost - a simple "not feeling a connection" is better<br/>
        • Respect boundaries and consent always
      </SectionContent>

      <SectionTitle>⚖️ Consequences</SectionTitle>
      <SectionContent>
        Violations may result in:<br/>
        • Warning and profile review<br/>
        • Temporary suspension<br/>
        • Permanent account termination<br/>
        • Reporting to authorities if laws are violated
      </SectionContent>

      <SectionTitle>🤝 Reporting & Support</SectionTitle>
      <SectionContent>
        Help us maintain a great community:<br/>
        • Report inappropriate behavior using the report button<br/>
        • Block users who make you uncomfortable<br/>
        • Contact support at <a href="mailto:community@flirteen.com" style={{ color: '#00E9FF' }}>community@flirteen.com</a><br/>
        • We review all reports promptly and take appropriate action
      </SectionContent>

      <SectionTitle>💙 Thank You</SectionTitle>
      <SectionContent>
        Thank you for helping make Flirteen a safe, welcoming space where people can find genuine connections. Together, we create a community where love can flourish.
      </SectionContent>
    </div>
  );

  const renderAboutContent = () => (
    <div>
      <PageHeader title="About Flirteen" icon="💕" />

      <SectionTitle>Our Story</SectionTitle>
      <SectionContent>
        Flirteen was born from a simple belief: everyone deserves to find meaningful connections. In a world of superficial swipes and endless scrolling, we created a platform that prioritizes genuine relationships over casual encounters.
      </SectionContent>

      <SectionTitle>🎯 Our Mission</SectionTitle>
      <SectionContent>
        To help people find authentic, lasting relationships by creating a safe, inclusive, and respectful dating environment. We believe in the power of genuine connections and meaningful conversations.
      </SectionContent>

      <SectionTitle>✨ What Makes Us Different</SectionTitle>
      <SectionContent>
        • <strong>Quality over Quantity:</strong> We focus on meaningful matches, not endless options<br/>
        • <strong>Safety First:</strong> Robust verification and reporting systems<br/>
        • <strong>Inclusive Community:</strong> Welcome to all backgrounds, orientations, and relationship goals<br/>
        • <strong>Privacy Protected:</strong> Your data security is our top priority<br/>
        • <strong>Real Connections:</strong> Encouraging deeper conversations and authentic interactions
      </SectionContent>

      <SectionTitle>🌟 Our Values</SectionTitle>
      <SectionContent>
        <strong>Authenticity:</strong> Be yourself, find someone who appreciates the real you<br/>
        <strong>Respect:</strong> Every interaction should be courteous and considerate<br/>
        <strong>Safety:</strong> Everyone deserves to feel secure while dating<br/>
        <strong>Inclusivity:</strong> Love comes in all forms, and everyone is welcome<br/>
        <strong>Privacy:</strong> Your personal information stays personal
      </SectionContent>

      <SectionTitle>🚀 Our Features</SectionTitle>
      <SectionContent>
        • Smart matching algorithm based on compatibility<br/>
        • Secure messaging system<br/>
        • Profile verification options<br/>
        • Advanced privacy controls<br/>
        • Premium features for enhanced experience<br/>
        • 24/7 customer support and safety monitoring
      </SectionContent>

      <SectionTitle>🌍 Join Our Community</SectionTitle>
      <SectionContent>
        Whether you're looking for a life partner, meaningful dating, or genuine friendship, Flirteen provides the tools and environment to help you connect with like-minded individuals who share your values and interests.
      </SectionContent>

      <SectionTitle>📞 Get in Touch</SectionTitle>
      <SectionContent>
        Have questions or feedback? We'd love to hear from you:<br/>
        • General inquiries: <a href="mailto:hello@flirteen.com" style={{ color: '#00E9FF' }}>hello@flirteen.com</a><br/>
        • Support: <a href="mailto:support@flirteen.com" style={{ color: '#00E9FF' }}>support@flirteen.com</a><br/>
        • Safety concerns: <a href="mailto:safety@flirteen.com" style={{ color: '#00E9FF' }}>safety@flirteen.com</a>
      </SectionContent>

      <SectionTitle>💙 Thank You</SectionTitle>
      <SectionContent>
        Thank you for being part of the Flirteen community. We're honored to be part of your journey toward finding meaningful connections and lasting love.
      </SectionContent>
    </div>
  );

  const renderContent = () => {
    switch(currentPage) {
      case 'terms': return renderTermsContent();
      case 'privacy': return renderPrivacyContent();
      case 'safety': return renderSafetyContent();
      case 'community': return renderCommunityContent();
      case 'about': return renderAboutContent();
      default: return renderTermsContent();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px 0'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Navigation */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '15px',
          padding: '20px',
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '10px'
          }}>
            {[
              { key: 'terms', label: '📋 Terms', title: 'Terms & Conditions' },
              { key: 'privacy', label: '🔒 Privacy', title: 'Privacy Policy' },
              { key: 'safety', label: '🛡️ Safety', title: 'Safety Guidelines' },
              { key: 'community', label: '👥 Community', title: 'Community Guidelines' },
              { key: 'about', label: '💕 About', title: 'About Us' }
            ].map(item => (
              <button
                key={item.key}
                onClick={() => {
                  setCurrentPage(item.key);
                  window.history.pushState({}, '', `/${item.key}`);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '25px',
                  border: 'none',
                  background: currentPage === item.key 
                    ? 'linear-gradient(90deg, #00E9FF, #8A2BE2)' 
                    : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  if (currentPage !== item.key) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseOut={(e) => {
                  if (currentPage !== item.key) {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
                title={item.title}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          padding: '40px',
          marginBottom: '40px'
        }}>
          {renderContent()}
        </div>

        {/* Back to App */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              color: 'white',
              border: 'none',
              borderRadius: '15px',
              padding: '15px 30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ← Back to Flirteen
          </button>
        </div>
      </div>
    </div>
  );
}

export default LegalPages;